import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { useOpdLookup, useOpdSchedules } from './opd.queries';
import { FieldLabel, LookupSelect } from './opd.shared';
import { formatOpdScheduleDate, formatOpdScheduleTime, todayInput } from './opd.utils';

export const OpdDoctorSchedulePage = () => {
  const doctors = useOpdLookup('doctors');
  const [doctorDraft, setDoctorDraft] = useState('');
  const [doctor, setDoctor] = useState('');
  const schedules = useOpdSchedules(doctor, todayInput(), '', true);
  const dates = useMemo(() => [...new Set((schedules.data ?? []).map((slot) => slot.date.slice(0, 10)))], [schedules.data]);
  const [selectedDate, setSelectedDate] = useState('');
  const activeDate = selectedDate || dates[0] || '';
  const visible = (schedules.data ?? []).filter((slot) => slot.date.slice(0, 10) === activeDate);
  const showNoSchedule = schedules.isSuccess && doctor !== '' && schedules.data.length === 0;
  const scheduleError = schedules.error instanceof ApiError ? schedules.error.message : 'Doctor schedule could not be loaded.';

  const search = (event: FormEvent) => {
    event.preventDefault();
    setDoctor(doctorDraft);
    setSelectedDate('');
  };

  const reset = () => {
    setDoctor('');
    setDoctorDraft('');
    setSelectedDate('');
  };

  return (
    <div className="page-stack opd-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">OPD</p>
          <h1>Doctor Schedule</h1>
          <p>Browse upcoming dates, availability, and book an enquiry directly.</p>
        </div>
      </div>

      <section className="content-card opd-card">
        <form className="opd-doctor-search" onSubmit={search}>
          <label>
            <FieldLabel required>Doctor</FieldLabel>
            <LookupSelect
              id="doctor-schedule-doctor"
              value={doctorDraft}
              options={doctors.data}
              placeholder={doctors.isLoading ? 'Loading doctors...' : 'Select Doctor'}
              clearable={false}
              disabled={doctors.isLoading || doctors.isError}
              onChange={setDoctorDraft}
            />
          </label>
          <button className="button button-primary" disabled={!doctorDraft || schedules.isFetching}>Search</button>
          <button className="button button-secondary" type="button" onClick={reset}>Reset</button>
        </form>

        {doctors.isError ? (
          <div className="inline-alert" role="alert">
            {doctors.error instanceof ApiError ? doctors.error.message : 'Doctor list could not be loaded.'}
          </div>
        ) : null}

        {schedules.isLoading ? <LoadingState /> : null}
        {schedules.isError ? (
          <ErrorState
            title="Unable to load doctor schedule"
            message={scheduleError}
            action={{ label: 'Try again', onClick: () => void schedules.refetch() }}
          />
        ) : null}
        {showNoSchedule ? (
          <EmptyState title="No schedule found" message="No active upcoming schedule is available for this doctor." />
        ) : null}

        {schedules.isSuccess && dates.length > 0 ? (
          <div className="opd-doctor-schedule">
            <aside>
              {dates.map((date) => (
                <button
                  key={date}
                  type="button"
                  className={activeDate === date ? 'active' : ''}
                  onClick={() => setSelectedDate(date)}
                >
                  &#9635; {formatOpdScheduleDate(date)}
                </button>
              ))}
            </aside>
            <div>
              <h2>{formatOpdScheduleDate(activeDate)}</h2>
              <div className="opd-doctor-schedule-grid opd-doctor-schedule-grid-compact">
                {visible.map((slot) => slot.available ? (
                  <Link key={slot.id} className="available" to={`/opd/appointments/new?doctor=${doctor}&slot=${slot.id}`}>
                    <strong>{formatOpdScheduleTime(slot.date, slot.fromTime)} - {formatOpdScheduleTime(slot.date, slot.toTime)}</strong>
                    <small>{slot.booked} / {slot.patientPerSlot}</small>
                  </Link>
                ) : (
                  <div key={slot.id} className="full">
                    <strong>{formatOpdScheduleTime(slot.date, slot.fromTime)} - {formatOpdScheduleTime(slot.date, slot.toTime)}</strong>
                    <span>{slot.booked} / {slot.patientPerSlot}</span>
                    {slot.status === 'inactive' ? <small>Deactivated</small> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};
